import React, { useState, useRef, useMemo, useEffect } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Popover,
  Box,
  Tooltip,
  useMediaQuery,
  Typography,
  Paper,
  List,
  ListItemButton,
  ClickAwayListener,
  Chip,
  Alert,
} from "@mui/material";
import { InsertEmoticon, AttachFile, Mic, Stop, FormatBold, Send, AutoAwesome, Close, InfoOutlined } from "@mui/icons-material";
import ChannelSelector from "./ChannelSelector";

const SUGGESTIONS_MAX_HEIGHT = 280;
const SUGGESTION_PREVIEW_LENGTH = 120;

/** Normaliza string para busca ignorando acentos (lowercase, sem diacríticos). */
function normalizeForSearch(s) {
  if (s == null || typeof s !== "string") return "";
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const MessageWhatsappChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  isRecording,
  startRecording,
  stopRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  suggestions = { templates: [], quickReplies: [] },
  isLoadingSuggestions = false,
  suggestionsError = null,
  requiresTemplate = false,
  selectedTemplate = null,
  onTemplateSelect,
}) => {
  const [formatAnchorEl, setFormatAnchorEl] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [windowAlertDismissed, setWindowAlertDismissed] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const highlightedItemRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const inputBorderRadius = 16; // consistente com o campo do chat
  const canUseAiSuggestion =
    !!onGenerateSuggestion && channelType !== "Xpro" && hasHistory;
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const hasFormatOptions = !!formatOptions?.[channelType]?.length;
  const hasTemplatesAvailable = (suggestions?.templates || []).length > 0;

  const combinedSuggestions = useMemo(() => {
    const list = [];
    (suggestions?.templates || []).forEach((tpl) => {
      const fullText = (tpl.fullText || tpl.preview || tpl.name || "").trim();
      const displayText = (tpl.preview || tpl.name || fullText || "").trim();
      if (displayText) {
        list.push({
          type: "template",
          id: tpl.id,
          name: tpl.name,
          displayText,
          fullText,
          raw: tpl,
        });
      }
    });
    (suggestions?.quickReplies || []).forEach((qr) => {
      const displayText = (qr.text || "").trim();
      if (displayText) {
        list.push({
          type: "quickReply",
          id: qr.id,
          name: qr.text,
          displayText,
          fullText: displayText,
          raw: qr,
        });
      }
    });
    return list;
  }, [suggestions?.templates, suggestions?.quickReplies]);

  const searchTrim = (message || "").trim();
  const filteredSuggestions = useMemo(() => {
    if (searchTrim.length === 0) return [];
    const normSearch = normalizeForSearch(searchTrim);
    return combinedSuggestions.filter(
      (item) =>
        (item.displayText && normalizeForSearch(item.displayText).includes(normSearch)) ||
        (item.name && normalizeForSearch(String(item.name)).includes(normSearch))
    );
  }, [combinedSuggestions, searchTrim]);

  const suggestionsOpen =
    filteredSuggestions.length > 0 && searchTrim.length >= 1 && !suggestionsDismissed;

  useEffect(() => {
    if (!suggestionsOpen) setHighlightedIndex(0);
    else setHighlightedIndex((i) => Math.min(i, filteredSuggestions.length - 1));
  }, [suggestionsOpen, filteredSuggestions.length]);

  // Rolagem para manter o item destacado visível ao navegar com setas
  useEffect(() => {
    if (!suggestionsOpen || highlightedIndex < 0) return;
    highlightedItemRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
      inline: "nearest",
    });
  }, [suggestionsOpen, highlightedIndex]);

  const handleFormatPickerOpen = (event) => setFormatAnchorEl(event.currentTarget);
  const handleFormatPickerClose = () => setFormatAnchorEl(null);

  const closeSuggestions = () => {
    setHighlightedIndex(0);
    setSuggestionsDismissed(true);
  };

  const selectSuggestion = (item) => {
    const textToUse =
      item.type === "template"
        ? (item.fullText || item.displayText)
        : item.displayText;

    setMessage(textToUse);

    if (item.type === "template" && typeof onTemplateSelect === "function") {
      onTemplateSelect({
        id: item.id,
        name: item.name,
        preview: item.displayText,
        raw: item.raw,
      });
    } else if (typeof onTemplateSelect === "function") {
      onTemplateSelect(null);
    }

    setSuggestionsDismissed(true);
    setHighlightedIndex(0);
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    if (searchTrim.length === 0) setSuggestionsDismissed(false);
  }, [searchTrim.length]);

  const handleApplyFormat = (format) => {
    const input = inputRef.current;
    if (input) {
      const [startTag, endTag] = format.split("texto");
      const { selectionStart, selectionEnd } = input;

      if (selectionStart !== null && selectionEnd !== null) {
        const beforeText = message.substring(0, selectionStart);
        const selectedText = message.substring(selectionStart, selectionEnd);
        const afterText = message.substring(selectionEnd);

        setMessage(`${beforeText}${startTag}${selectedText}${endTag}${afterText}`);
      }
    }
    handleFormatPickerClose();
  };

  const handleInputKeyDown = (e) => {
    if (suggestionsOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => (i < filteredSuggestions.length - 1 ? i + 1 : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : filteredSuggestions.length - 1));
        return;
      }
      if (e.key === "Enter" && filteredSuggestions[highlightedIndex]) {
        e.preventDefault();
        selectSuggestion(filteredSuggestions[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestions();
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const previewText = (text) => {
    if (!text) return "";
    const s = String(text).trim().replace(/\s+/g, " ");
    if (s.length <= SUGGESTION_PREVIEW_LENGTH) return s;
    return `${s.slice(0, SUGGESTION_PREVIEW_LENGTH)}…`;
  };

  /** Encontra trechos em `text` que correspondem a `search` ignorando acentos e retorna partes para destacar em negrito. */
  const highlightMatch = (text, search) => {
    if (!text || !search || !String(search).trim()) return text;
    const str = String(text);
    const term = String(search).trim();
    if (!term) return str;
    const normSearch = normalizeForSearch(term);
    if (!normSearch) return str;
    const chars = Array.from(str);
    let norm = "";
    const normToOrig = [];
    for (let i = 0; i < chars.length; i++) {
      const n = normalizeForSearch(chars[i]);
      if (n.length > 0) {
        for (let j = 0; j < n.length; j++) {
          normToOrig.push({ start: i, end: i + 1 });
          norm += n[j];
        }
      }
    }
    const spans = [];
    let idx = 0;
    while (idx < norm.length) {
      const found = norm.indexOf(normSearch, idx);
      if (found === -1) break;
      const end = found + normSearch.length;
      const oStart = normToOrig[found].start;
      const oEnd = normToOrig[end - 1].end;
      spans.push({ start: oStart, end: oEnd });
      idx = end;
    }
    if (spans.length === 0) return str;
    const parts = [];
    let last = 0;
    spans.forEach(({ start, end }) => {
      if (start > last) {
        parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last, start).join("") });
      }
      parts.push({ key: `m-${start}`, type: "mark", value: chars.slice(start, end).join("") });
      last = end;
    });
    if (last < chars.length) {
      parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last).join("") });
    }
    return parts.map((p) =>
      p.type === "mark" ? (
        <Box
          component="span"
          key={p.key}
          sx={{
            backgroundColor: "grey.200",
            color: "text.primary",
            borderRadius: 0.5,
            px: 0.25,
            fontWeight: 700,
          }}
        >
          {p.value}
        </Box>
      ) : (
        <span key={p.key}>{p.value}</span>
      )
    );
  };

  return (
    <ClickAwayListener onClickAway={closeSuggestions}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 0.5 : 1,
          paddingBottom: 0,
        }}
      >
        {requiresTemplate && !windowAlertDismissed && (
          <Alert
            severity="info"
            icon={<InfoOutlined sx={{ fontSize: 22 }} />}
            onClose={() => setWindowAlertDismissed(true)}
            sx={{
              mb: 2,
              py: 1.5,
              px: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.light",
              "& .MuiAlert-message": { width: "100%", padding: 0 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "info.dark" }}>
              {hasHistory ? "Janela de 24 horas do WhatsApp fechada" : "Primeiro contato pelo WhatsApp"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
              {hasHistory ? (
                hasTemplatesAvailable ? (
                  <>
                    Após 24h da última mensagem do cliente, o WhatsApp só permite enviar mensagens através de{" "}
                    <strong>templates de reengajamento</strong>. Digite no campo abaixo para ver os templates disponíveis
                    ou aguarde o cliente enviar uma nova mensagem.
                  </>
                ) : (
                  <>
                    Após 24h da última mensagem do cliente, o WhatsApp só permite enviar mensagens através de{" "}
                    <strong>templates de reengajamento</strong>. Parece que você ainda não possui templates configurados
                    para este canal. Entre em contato com o nosso suporte e solicite o cadastro de templates de
                    reengajamento antes de retomar conversas fora da janela de 24h.
                  </>
                )
              ) : hasTemplatesAvailable ? (
                <>
                  Esta solicitação ainda não tem mensagens pelo WhatsApp. Para iniciar a conversa com o cliente, o
                  WhatsApp exige que a primeira mensagem seja enviada usando um{" "}
                  <strong>template de reengajamento</strong>. Digite no campo abaixo para buscar um template que apresente
                  sua empresa, explique por que você está entrando em contato e oriente claramente qual é o próximo passo
                  para o cliente.
                </>
              ) : (
                <>
                  Esta solicitação ainda não tem mensagens pelo WhatsApp. Para iniciar a conversa com o cliente, o
                  WhatsApp exige que a primeira mensagem seja enviada usando um{" "}
                  <strong>template de reengajamento</strong>. Parece que você ainda não possui templates configurados para
                  este canal: antes de falar com o cliente, entre em contato com o nosso suporte e solicite o cadastro de
                  templates alinhados à sua operação (apresentação, motivo do contato e próximos passos para o cliente).
                </>
              )}
            </Typography>
          </Alert>
        )}
        <Box ref={anchorRef} sx={{ position: "relative", width: "100%" }}>
          {suggestionsOpen && (
            <Paper
              elevation={8}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: "100%",
                mb: 0.5,
                maxHeight: SUGGESTIONS_MAX_HEIGHT,
                overflow: "hidden",
                zIndex: 1300,
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", maxHeight: SUGGESTIONS_MAX_HEIGHT }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    py: 1,
                    pr: 1,
                    pl: 2,
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={closeSuggestions}
                    sx={{
                      color: "text.secondary",
                      "&:hover": { color: "text.primary", backgroundColor: "action.hover" },
                    }}
                    aria-label="Fechar sugestões"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Box>
                <List dense disablePadding sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
                  {filteredSuggestions.map((item, idx) => (
                    <ListItemButton
                      key={`${item.type}-${item.id}-${idx}`}
                      ref={idx === highlightedIndex ? highlightedItemRef : null}
                      selected={idx === highlightedIndex}
                      onMouseEnter={() => setHighlightedIndex(idx)}
                      onClick={() => selectSuggestion(item)}
                      sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        py: 1,
                        borderBottom: idx < filteredSuggestions.length - 1 ? "1px solid" : "none",
                        borderColor: "divider",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                        <Chip
                          size="small"
                          label={item.type === "template" ? "Template" : "Mensagem rápida"}
                          variant="outlined"
                          sx={{
                            flexShrink: 0,
                            height: 20,
                            fontSize: "0.7rem",
                            ...(item.type === "template"
                              ? { borderColor: "primary.main", color: "primary.main" }
                              : { borderColor: "secondary.main", color: "secondary.main" }),
                          }}
                        />
                        <Typography variant="body2" component="span" color="text.primary" sx={{ flex: 1 }}>
                          {highlightMatch(previewText(item.displayText), searchTrim)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Paper>
          )}
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            variant="outlined"
            placeholder={
              requiresTemplate
                ? hasTemplatesAvailable
                  ? "Use um template de reengajamento (digite para buscar)"
                  : "Você ainda não possui templates de reengajamento cadastrados. Fale com o suporte para configurá-los."
                : channelType === "Xpro"
                ? "Registrar uma anotação"
                : `Envie uma mensagem via ${channelType}`
            }
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleInputKeyDown}
            InputProps={{
              style: {
                borderRadius: `${inputBorderRadius}px`,
                fontSize: isMobile ? "0.95rem" : "1rem",
              },
              inputRef: inputRef,
              endAdornment: !isMobile && ( // Exibe botões ao lado apenas no desktop
                <InputAdornment position="end">
                  {canUseAiSuggestion && (
                    <Tooltip
                      title={
                        !hasHistory
                          ? "Disponível após a conversa ter histórico."
                          : isGeneratingSuggestion
                          ? "Gerando resposta..."
                          : "Sugerir resposta com IA"
                      }
                    >
                      <span>
                        <IconButton
                          onClick={onGenerateSuggestion}
                          disabled={aiDisabled}
                        >
                          <AutoAwesome />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <IconButton onClick={handleEmojiPickerOpen}>
                    <InsertEmoticon />
                  </IconButton>
                  {hasFormatOptions && (
                    <IconButton onClick={handleFormatPickerOpen}>
                      <FormatBold />
                    </IconButton>
                  )}
                  <IconButton onClick={openFilePicker}>
                    <AttachFile />
                  </IconButton>
                  <IconButton onClick={isRecording ? stopRecording : startRecording}>
                    {isRecording ? <Stop /> : <Mic />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: `${inputBorderRadius}px !important`,
                overflow: "hidden",
                clipPath: `inset(0 round ${inputBorderRadius}px)`,
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#b0b0b0",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#888888",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: `${inputBorderRadius}px !important`,
                overflow: "hidden",
                clipPath: `inset(0 round ${inputBorderRadius}px)`,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderRadius: `${inputBorderRadius}px !important`,
              },
              "& textarea": {
                overflowY: "auto",
                resize: "none",
                boxSizing: "border-box",
                borderRadius: "inherit",
                overflowX: "hidden",
                padding: "4px 14px",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#b0b0b0",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#888888",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
              },
            }}
          />
        </Box>

        {isMobile && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 0.5,
              px: 0.5,
              gap: 0.5,
            }}
          >
            <ChannelSelector
              selectedChannel={selectedChannel}
              setSelectedChannel={setSelectedChannel}
            />
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                flexGrow: 1,
                justifyContent: "center",
              }}
            >
              {canUseAiSuggestion && (
                <IconButton
                  size="small"
                  onClick={onGenerateSuggestion}
                  disabled={aiDisabled}
                >
                  <AutoAwesome fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={handleEmojiPickerOpen}>
                <InsertEmoticon fontSize="small" />
              </IconButton>
              {hasFormatOptions && (
                <IconButton size="small" onClick={handleFormatPickerOpen}>
                  <FormatBold fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={openFilePicker}>
                <AttachFile fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? <Stop fontSize="small" /> : <Mic fontSize="small" />}
              </IconButton>
            </Box>
            <IconButton
              onClick={handleSendMessage}
              disabled={!message.trim() && !file && !audioURL}
              sx={{
                backgroundColor: "#3f51b5",
                color: "#fff",
                borderRadius: "50%",
                p: 1,
                ml: 0.5,
                "&:hover": { backgroundColor: "#303f9f" },
              }}
            >
              <Send fontSize="small" />
            </IconButton>
          </Box>
        )}
        {hasFormatOptions && (
          <Popover
            open={Boolean(formatAnchorEl)}
            anchorEl={formatAnchorEl}
            onClose={handleFormatPickerClose}
            anchorOrigin={{ vertical: "top", horizontal: "left" }}
            transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box p={2} display="flex" gap={1}>
              {formatOptions[channelType]?.map((format, index) => (
                <Tooltip key={index} title={format.tooltip}>
                  <IconButton onClick={() => handleApplyFormat(format.action)}>
                    {format.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Popover>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default MessageWhatsappChannel;
